import { useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleCheck } from "@fortawesome/pro-duotone-svg-icons"
import { faLeftLong } from "@fortawesome/sharp-solid-svg-icons"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Loader from "../../../components/Loader"
import Header from "../../../components/Header"

/**
 * Verifying / verified / wrong-code states for the survey lane, and the hop to
 * the first survey page. Kept separate from the commissioning `LoginStates` so
 * neither lane's redirect can be changed by an edit meant for the other.
 */
type SurveyLoginStatesProps = {
    loading: boolean
    error?: boolean
}

const SURVEY_LANDING = "/site-survey/survey-details"

const SurveyLoginStates = ({ loading, error }: SurveyLoginStatesProps) => {
    const navigate = useNavigate()
    const { t: translate } = useTranslation()

    const [isVerifying, setIsVerifying] = useState(true)

    useEffect(() => {
        if (error) {
            const failed = setTimeout(() => setIsVerifying(false), 3000)
            return () => clearTimeout(failed)
        }

        // Show the spinner, then the tick, then move on.
        const verified = setTimeout(() => setIsVerifying(false), 3000)
        const redirect = setTimeout(() => navigate(SURVEY_LANDING), 5000)
        return () => {
            clearTimeout(verified)
            clearTimeout(redirect)
        }
    }, [loading, error, navigate])

    const renderState = () => {
        if (isVerifying) return <Loader text={translate("verifyingText")} />

        if (error) {
            return (
                <Box>
                    <Header
                        heading={translate("wrongCode")}
                        subHeading={translate("wrongCodeSubText")}
                    />
                    <Button
                        sx={{ marginTop: "60px", minWidth: "165px" }}
                        size="small"
                        variant="outlined"
                        onClick={() => window.location.reload()}
                    >
                        <FontAwesomeIcon
                            icon={faLeftLong}
                            style={{ color: "#000", height: "8px", width: "12px", marginRight: "8px" }}
                        />
                        <Typography fontSize="18px" fontWeight={500}>
                            {translate("tryAgain")}
                        </Typography>
                    </Button>
                </Box>
            )
        }

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    justifyContent: "center",
                    height: "calc(100% - 104px)",
                }}
            >
                <FontAwesomeIcon
                    icon={faCircleCheck}
                    style={{
                        "--fa-primary-color": "#34c759",
                        "--fa-secondary-color": "#34c759",
                        height: "24px",
                        width: "24px",
                    } as any}
                />
                <Typography fontSize="18px">{translate("verificationSuccessful")}</Typography>
            </Box>
        )
    }

    return (
        <Box textAlign="center" height="calc(100vh - 152px)" alignContent="center">
            {renderState()}
        </Box>
    )
}

export default SurveyLoginStates
